import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
    HiOutlineHome,
    HiOutlineBriefcase,
    HiOutlineSearch,
    HiOutlineChatAlt2,
    HiOutlineCog,
    HiPlus,
} from "react-icons/hi";

const BrandCampaigns = () => {
    const [activeTab, setActiveTab] = useState("Active");

    // Mock Data representing campaigns from backend
    const allCampaigns = [
        {
            id: 1,
            title: "Summer Collection Launch",
            status: "Active",
            applicants: 12,
            budget: "$5,000",
            deadline: "Oct 24, 2024"
        },
        {
            id: 2,
            title: "Holiday Special Promo",
            status: "Pending", // Was 'Draft', changed to Pending to match requirements
            applicants: 0,
            budget: "$2,500",
            deadline: "Dec 15, 2024"
        },
        {
            id: 3,
            title: "Influencer Brand Awareness",
            status: "Completed",
            applicants: 45,
            budget: "$10,000",
            deadline: "Sep 01, 2024"
        },
        {
            id: 4,
            title: "Winter collection promo",
            status: "Active",
            applicants: 3,
            budget: "$2,500",
            deadline: "Jan 15, 2025"
        },
        {
            id: 5,
            title: "Tech Gadget Review",
            status: "Pending",
            applicants: 2,
            budget: "$8,000",
            deadline: "Mar 10, 2025"
        },
        {
            id: 6,
            title: "Fitness App Launch",
            status: "Completed",
            applicants: 120,
            budget: "$15,000",
            deadline: "Aug 15, 2024"
        }
    ];

    const filteredCampaigns = allCampaigns.filter(campaign => campaign.status === activeTab);

    return (
        <div className="flex h-screen bg-[#FDFBF7]">
            {/* Sidebar */}
            <aside className="w-64 bg-white border-r border-gray-100 flex flex-col hidden md:flex">
                <div className="p-6 flex items-center gap-2 text-black font-bold text-xl">
                    <img src="/logo.png" alt="Braccet" className="w-8 h-8 object-contain" />
                    <span>Braccet</span>
                </div>

                <nav className="flex-1 px-4 py-6 space-y-2">
                    <NavItem to="/brand/dashboard" icon={<HiOutlineHome />} label="Overview" />
                    <NavItem to="/brand/campaigns" icon={<HiOutlineBriefcase />} label="Campaigns" active />
                    <NavItem to="/brand/search" icon={<HiOutlineSearch />} label="Find Creators" />
                    <NavItem to="/brand/messages" icon={<HiOutlineChatAlt2 />} label="Messages" />
                    <NavItem to="/brand/settings" icon={<HiOutlineCog />} label="Settings" />
                </nav>

                <div className="p-4 border-t border-gray-100">
                    <div className="flex items-center gap-3 p-2 rounded-xl hover:bg-gray-50 cursor-pointer">
                        <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-bold">
                            B
                        </div>
                        <div>
                            <p className="text-sm font-bold text-gray-900">Brand Name</p>
                            <p className="text-xs text-gray-500">brand@example.com</p>
                        </div>
                    </div>
                </div>
            </aside>

            {/* Main Content */}
            <main className="flex-1 overflow-hidden flex flex-col h-screen">
                <header className="bg-white/80 backdrop-blur-md border-b border-gray-100 px-6 py-4 flex justify-between items-center shrink-0">
                    <h1 className="text-2xl font-bold text-gray-900">Campaigns</h1>
                    <Link to="/brand/create-campaign" className="bg-[#0044CC] text-white px-4 py-2 rounded-lg font-medium text-sm flex items-center gap-2 hover:bg-blue-700 transition-colors">
                        <HiPlus className="text-lg" />
                        New Campaign
                    </Link>
                </header>

                <div className="flex-1 overflow-y-auto p-6">
                    <div className="max-w-7xl mx-auto space-y-6">

                        {/* Tabs */}
                        <div className="flex gap-8 border-b border-gray-200">
                            {['Active', 'Pending', 'Completed'].map((tab) => (
                                <button
                                    key={tab}
                                    onClick={() => setActiveTab(tab)}
                                    className={`pb-3 text-sm font-medium transition-colors relative ${activeTab === tab
                                        ? 'text-[#0044CC] font-bold'
                                        : 'text-gray-500 hover:text-gray-700'
                                        }`}
                                >
                                    {tab}
                                    {activeTab === tab && (
                                        <div className="absolute bottom-0 left-0 w-full h-0.5 bg-[#0044CC] rounded-t-full" />
                                    )}
                                </button>
                            ))}
                        </div>

                        {/* Filters */}
                        <div className="flex gap-4 items-center">
                            <div className="relative flex-1 max-w-md">
                                <HiOutlineSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-lg" />
                                <input
                                    type="text"
                                    placeholder="Search campaigns..."
                                    className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                            </div>

                        </div>

                        {/* Campaign List */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {filteredCampaigns.length > 0 ? (
                                filteredCampaigns.map((campaign) => (
                                    <CampaignCard
                                        key={campaign.id}
                                        title={campaign.title}
                                        status={campaign.status}
                                        applicants={campaign.applicants}
                                        budget={campaign.budget}
                                        deadline={campaign.deadline}
                                    />
                                ))
                            ) : (
                                <div className="col-span-full py-12 text-center text-gray-500 bg-white rounded-2xl border border-gray-100 border-dashed">
                                    <p>No {activeTab.toLowerCase()} campaigns found.</p>
                                </div>
                            )}
                        </div>

                    </div>
                </div>
            </main>
        </div>
    );
};

const NavItem = ({ to, icon, label, active }) => (
    <Link to={to} className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors ${active ? 'bg-blue-50 text-[#0044CC]' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'}`}>
        <span className="text-xl">{icon}</span>
        {label}
    </Link>
);

const CampaignCard = ({ title, status, applicants, budget, deadline }) => (
    <div className="bg-white border border-gray-100 rounded-2xl p-5 shadow-sm hover:shadow-md transition-shadow">
        <div className="flex justify-between items-start mb-4">
            <div className={`px-3 py-1 rounded-full text-xs font-bold 
                ${status === 'Active' ? 'bg-green-100 text-green-700' :
                    status === 'Pending' ? 'bg-yellow-100 text-yellow-700' :
                        status === 'Completed' ? 'bg-blue-100 text-blue-700' :
                            'bg-gray-100 text-gray-700'}`}>
                {status}
            </div>
            <button className="text-gray-400 hover:text-gray-600">
                <HiOutlineCog className="text-lg" />
            </button>
        </div>
        <h3 className="font-bold text-gray-900 text-lg mb-2">{title}</h3>
        <div className="space-y-2 text-sm text-gray-600 mb-4">
            <div className="flex justify-between">
                <span>Budget</span>
                <span className="font-medium text-gray-900">{budget}</span>
            </div>
            <div className="flex justify-between">
                <span>Deadline</span>
                <span className="font-medium text-gray-900">{deadline}</span>
            </div>
            <div className="flex justify-between">
                <span>Applicants</span>
                <span className="font-medium text-gray-900">{applicants}</span>
            </div>
        </div>
        <Link to={`/brand/campaigns/${title.replace(/\s+/g, '-').toLowerCase()}`} className="block w-full text-center py-2 border border-gray-200 rounded-lg text-sm font-bold text-gray-700 hover:bg-gray-50">
            Manage Campaign
        </Link>
    </div>
);

export default BrandCampaigns;
